import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SyncStatusBadge from '../../../components/ui/SyncStatusBadge';

// Provider badge component
const ProviderBadge = ({ provider }) => {
  if (!provider || provider === 'manual') return null;
  const config = {
    strava: { label: 'Strava', color: 'bg-orange-500/10 text-orange-500 border border-orange-500/20' },
    garmin: { label: 'Garmin', color: 'bg-blue-500/10 text-blue-500 border border-blue-500/20' },
    whoop: { label: 'WHOOP', color: 'bg-red-500/10 text-red-500 border border-red-500/20' },
    oura: { label: 'Oura', color: 'bg-purple-500/10 text-purple-500 border border-purple-500/20' },
    fitbit: { label: 'Fitbit', color: 'bg-teal-500/10 text-teal-500 border border-teal-500/20' },
  };
  const cfg = config?.[provider?.toLowerCase()];
  if (!cfg) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg?.color}`}>
      <Icon name="Zap" size={10} />
      {cfg?.label}
    </span>
  );
};

/** Badge shown when a Strava activity was merged with a manual log entry */
const MergedBadge = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-500/10 text-violet-500 border border-violet-500/20">
    <Icon name="GitMerge" size={10} />
    Merged
  </span>
);

const ActivityTable = ({ activities, onEditActivity, onDeleteActivity }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedActivities = [...activities]?.sort((a, b) => {
    if (sortConfig?.key === 'date') {
      const dateA = new Date(a.date || a.start_time);
      const dateB = new Date(b.date || b.start_time);
      return sortConfig?.direction === 'asc' ? dateA - dateB : dateB - dateA;
    }
    if (sortConfig?.key === 'duration' || sortConfig?.key === 'calories' || sortConfig?.key === 'steps' || sortConfig?.key === 'distance') {
      return sortConfig?.direction === 'asc'
        ? (a?.[sortConfig?.key] || 0) - (b?.[sortConfig?.key] || 0)
        : (b?.[sortConfig?.key] || 0) - (a?.[sortConfig?.key] || 0);
    }
    return sortConfig?.direction === 'asc'
      ? (a?.[sortConfig?.key] || '')?.localeCompare(b?.[sortConfig?.key] || '')
      : (b?.[sortConfig?.key] || '')?.localeCompare(a?.[sortConfig?.key] || '');
  });

  const totalPages = Math.ceil(sortedActivities?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedActivities = sortedActivities?.slice(startIndex, startIndex + itemsPerPage);

  const getActivityIcon = (type) => {
    const iconMap = {
      walking: 'Footprints',
      running: 'Zap',
      run: 'Zap',
      cycling: 'Bike',
      ride: 'Bike',
      virtualride: 'Bike',
      swimming: 'Waves',
      swim: 'Waves',
      yoga: 'Heart',
      strength: 'Dumbbell',
      weighttraining: 'Dumbbell',
      cardio: 'Activity',
      hike: 'Mountain',
      workout: 'Activity',
    };
    return iconMap?.[type?.toLowerCase()] || 'Activity';
  };

  const getIntensityColor = (intensity) => {
    switch (intensity) {
      case 'low': return 'text-success bg-success/10';
      case 'moderate': return 'text-warning bg-warning/10';
      case 'high': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const formatDistance = (distance) => {
    if (!distance) return null;
    const km = distance / 1000;
    return km >= 1 ? `${km?.toFixed(2)} km` : `${distance} m`;
  };

  const formatDuration = (seconds, minutes) => {
    if (seconds) {
      const m = Math.floor(seconds / 60);
      return `${m} min`;
    }
    if (minutes) return `${minutes} min`;
    return 'N/A';
  };

  const getActivityDate = (activity) => {
    return activity?.date || activity?.start_time;
  };

  const SortButton = ({ column, children }) => (
    <button
      onClick={() => handleSort(column)}
      className="flex items-center space-x-1 text-left font-medium text-foreground hover:text-primary transition-colors duration-200"
    >
      <span>{children}</span>
      <div className="flex flex-col">
        <Icon
          name="ChevronUp"
          size={12}
          className={`${sortConfig?.key === column && sortConfig?.direction === 'asc' ? 'text-primary' : 'text-muted-foreground'}`}
        />
        <Icon
          name="ChevronDown"
          size={12}
          className={`${sortConfig?.key === column && sortConfig?.direction === 'desc' ? 'text-primary' : 'text-muted-foreground'} -mt-1`}
        />
      </div>
    </button>
  );

  const isStravaActivity = (activity) => activity?.provider && activity?.provider !== 'manual';

  return (
    <div className="bg-card rounded-xl shadow-wellness elevation-1 border border-border/40 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center justify-center w-10 h-10 bg-secondary/10 rounded-lg">
              <Icon name="List" size={20} className="text-secondary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Activity Log</h3>
              <p className="text-sm text-muted-foreground">
                {activities?.length} activities recorded
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-4 text-left">
                <SortButton column="date">Date</SortButton>
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton column="type">Activity</SortButton>
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton column="distance">Distance</SortButton>
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton column="duration">Duration</SortButton>
              </th>
              <th className="px-6 py-4 text-left">
                <SortButton column="calories">Calories</SortButton>
              </th>
              <th className="px-6 py-4 text-left">Source</th>
              <th className="px-6 py-4 text-left">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40">
            {paginatedActivities?.map((activity) => (
              <tr key={activity?.id} className="hover:bg-muted/20 transition-colors duration-200">
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="font-medium text-foreground">
                      {new Date(getActivityDate(activity))?.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="text-muted-foreground">
                      {new Date(getActivityDate(activity))?.toLocaleDateString('en-US', {
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
                      <Icon name={getActivityIcon(activity?.type || activity?.activity_type)} size={16} className="text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground capitalize">
                        {activity?.activity_type || activity?.type}
                      </div>
                      <div className="text-sm text-muted-foreground">{activity?.name || activity?.workout_name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">
                    {formatDistance(activity?.distance) || '—'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">
                    {formatDuration(activity?.duration_seconds, activity?.duration || activity?.duration_minutes)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm font-medium text-foreground">
                    {activity?.calories ? `${activity?.calories} cal` : '—'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col gap-1">
                    {isStravaActivity(activity) ? (
                      <ProviderBadge provider={activity?.provider} />
                    ) : (
                      <span className="text-xs text-muted-foreground">Manual</span>
                    )}
                    {activity?._merged && <MergedBadge />}
                  </div>
                </td>
                <td className="px-6 py-4">
                  {isStravaActivity(activity) ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                      <Icon name="CheckCircle" size={10} />
                      Synced
                    </span>
                  ) : (
                    <SyncStatusBadge status={activity?.syncStatus || 'synced'} size="sm" />
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end space-x-2">
                    {!isStravaActivity(activity) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditActivity(activity)}
                        iconName="Edit2"
                        className="text-muted-foreground hover:text-foreground"
                      />
                    )}
                    {!isStravaActivity(activity) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onDeleteActivity(activity?.id)}
                        iconName="Trash2"
                        className="text-muted-foreground hover:text-error"
                      />
                    )}
                    {isStravaActivity(activity) && !activity?._merged && (
                      <span className="text-xs text-muted-foreground italic">Read-only</span>
                    )}
                    {activity?._merged && (
                      <span className="text-xs text-violet-500 italic">Strava + Manual</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border/40">
        {paginatedActivities?.map((activity) => (
          <div key={activity?.id} className="p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
                  <Icon name={getActivityIcon(activity?.type || activity?.activity_type)} size={20} className="text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-medium text-foreground capitalize">{activity?.activity_type || activity?.type}</h4>
                    <ProviderBadge provider={activity?.provider} />
                    {activity?._merged && <MergedBadge />}
                  </div>
                  <p className="text-sm text-muted-foreground">{activity?.name || activity?.workout_name}</p>
                </div>
              </div>
              {!isStravaActivity(activity) && (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditActivity(activity)}
                    iconName="Edit2"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteActivity(activity?.id)}
                    iconName="Trash2"
                    className="text-error"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Date:</span>
                <span className="ml-2 font-medium text-foreground">
                  {new Date(getActivityDate(activity))?.toLocaleDateString()}
                </span>
              </div>
              {formatDistance(activity?.distance) && (
                <div>
                  <span className="text-muted-foreground">Distance:</span>
                  <span className="ml-2 font-medium text-foreground">{formatDistance(activity?.distance)}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Duration:</span>
                <span className="ml-2 font-medium text-foreground">
                  {formatDuration(activity?.duration_seconds, activity?.duration || activity?.duration_minutes)}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Calories:</span>
                <span className="ml-2 font-medium text-foreground">
                  {activity?.calories ? `${activity?.calories} cal` : '—'}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/40">
              {isStravaActivity(activity) ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">
                  <Icon name="CheckCircle" size={10} />
                  Synced
                </span>
              ) : (
                <SyncStatusBadge status={activity?.syncStatus || 'synced'} size="sm" />
              )}
              {activity?.intensity && (
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getIntensityColor(activity?.intensity)}`}>
                  {activity?.intensity}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-border/40 bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, activities?.length)} of {activities?.length} activities
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                iconName="ChevronLeft"
              />
              <span className="text-sm font-medium text-foreground">
                {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                iconName="ChevronRight"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;